/*************************************************************
 * main.web.jsx - Web Entry Point for Analytics Dashboard with Authentication
 * This is the web-specific version that includes React Router and authentication
 * The Electron version (main.jsx) remains separate and unchanged
 *************************************************************/

import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import Login from './components/Login.jsx';
import ProtectedRoute from './components/ProtectedRoute';
import App from './App';

// Note: authUtils.js is loaded via script tag in index.web.html
// It wraps the global fetch automatically

console.log('[Vite Web] Starting Analytics Dashboard with Authentication...');

// Theme management
window.applyTheme = (themeName) => {
    console.log('[Theme] Applying theme:', themeName);
    document.body.classList.remove('theme-light', 'theme-dark', 'theme-system');
    document.body.classList.add(`theme-${themeName}`);
    
    if (themeName === 'system') {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        document.documentElement.dataset.theme = prefersDark ? 'dark' : 'light';
        console.log('[Theme] System theme detected as:', prefersDark ? 'dark' : 'light');
    } else {
        document.documentElement.dataset.theme = themeName;
    }
};

// Root component with routing
const Root = () => {
    return (
        <BrowserRouter>
            <AuthProvider>
                <Routes>
                    {/* Login route */}
                    <Route path="/login" element={<Login />} />
                    
                    {/* Protected dashboard route */}
                    <Route 
                        path="/dashboard" 
                        element={
                            <ProtectedRoute>
                                <App />
                            </ProtectedRoute>
                        } 
                    />
                    
                    {/* Default route - redirect to dashboard (which will redirect to login if not authenticated) */}
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    
                    {/* Catch all - redirect to dashboard */}
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
            </AuthProvider>
        </BrowserRouter>
    );
};

// Initialize the app
const container = document.getElementById('app-root');
if (!container) {
    throw new Error('Could not find app-root element');
}

const root = createRoot(container);
root.render(<Root />);

console.log('[Vite Web] Analytics Dashboard with authentication rendered successfully');